import { useEffect, useMemo, useState } from "react";
import { EventAnalytics } from "../../../../../local_modules/event-analytics/src";
import { useReportsStore } from "../../../../../hooks/redux/reports";
import { useCurrentOrganizationStore } from "../../../../../hooks/redux/currentOrganization";

const PRESET_TO_RANGE = (preset, custom) => {
  if (preset === "custom" && custom) return custom;

  const to = Math.floor(Date.now() / 1000);
  let from;
  switch (preset) {
    case "today": {
      const start = new Date();
      start.setHours(0, 0, 0, 0);
      from = Math.floor(start.getTime() / 1000);
      break;
    }
    case "24h":
      from = to - 24 * 3600;
      break;
    case "7d":
      from = to - 7 * 86400;
      break;
    case "90d":
      from = to - 90 * 86400;
      break;
    case "30d":
    default:
      from = to - 30 * 86400;
  }
  return { from, to };
};

// Mirrors the frontend tier table agreed on with the resolution-bucketing design —
// only used to decide whether to show the sort-by-hourly path; the backend is the
// actual source of truth for `resolution`/`resolutionLocked` in the response.
const EventAnalyticsContainer = () => {
  const { currentOrganization } = useCurrentOrganizationStore();
  const {
    reports,
    fetchEventsStats,
    fetchEventsTimeSeriesStats,
    fetchEventSeriesStats,
  } = useReportsStore();

  const [preset, setPreset] = useState("30d");
  const [customRange, setCustomRange] = useState(null);
  const [selectedEventUuid, setSelectedEventUuid] = useState(null);
  const [sort, setSort] = useState({ field: "impressions", direction: "desc" });
  const [page, setPage] = useState(1);

  const { from, to } = useMemo(
    () => PRESET_TO_RANGE(preset, customRange),
    [preset, customRange]
  );

  // Table fetch — re-runs on range, sort, or page change.
  useEffect(() => {
    fetchEventsStats({
      from,
      to,
      sortField: sort.field,
      sortDirection: sort.direction,
      page,
      limit: 10,
    });
  }, [from, to, sort.field, sort.direction, page, fetchEventsStats]);

  // Aggregate chart fetch — re-runs on range change only.
  useEffect(() => {
    fetchEventsTimeSeriesStats(from, to);
  }, [from, to, fetchEventsTimeSeriesStats]);

  // Selected-event overlay series — only fetched when something is selected.
  useEffect(() => {
    if (selectedEventUuid) {
      fetchEventSeriesStats(from, to, selectedEventUuid);
    }
  }, [from, to, selectedEventUuid, fetchEventSeriesStats]);

  const handleDateRangeChange = (next) => {
    if (next.preset === "custom" && next.from && next.to) {
      setCustomRange({ from: next.from, to: next.to });
    }
    setPreset(next.preset);
    setPage(1);
    setSelectedEventUuid(null);
  };

  const selectedEvent = useMemo(() => {
    const items = reports.eventsCollection?.items || [];
    const base = items.find((e) => e.event_uuid === selectedEventUuid);
    if (!base) {
      return null;
    }
    return { ...base, series: reports.eventSeriesCollection || [] };
  }, [
    reports.eventsCollection,
    reports.eventSeriesCollection,
    selectedEventUuid,
  ]);

  return (
    <EventAnalytics
      dateRange={{
        preset,
        from: new Date(from * 1000).toISOString(),
        to: new Date(to * 1000).toISOString(),
      }}
      onDateRangeChange={handleDateRangeChange}
      resolution={reports.eventsTimeSeriesResolution || "daily"}
      resolutionLocked={reports.eventsTimeSeriesResolutionLocked}
      chartSeries={reports.eventsTimeSeriesCollection}
      selectedEvent={selectedEvent}
      onSelectEvent={setSelectedEventUuid}
      events={reports.eventsCollection}
      sort={sort}
      onSortChange={(next) => {
        setSort(next);
        setPage(1);
      }}
      page={page}
      onPageChange={setPage}
      loading={{
        chart:
          reports.fetchEventsTimeSeries ||
          (!!selectedEventUuid && reports.fetchEventSeries),
        table: reports.fetchEventsStats,
      }}
      error={{
        chart:
          reports.fetchEventsTimeSeriesError?.message ||
          reports.fetchEventSeriesError?.message ||
          null,
        table: reports.fetchEventsStatsError?.message || null,
      }}
      billingPath="/billing"

    />
  );
};

export default EventAnalyticsContainer;
